<script setup lang="ts">
/*
 * Page nav with page-size dropdown, prev/next, and a "X–Y of N" summary.
 * Drives the bottom-of-table pagination on Pay Input center, employee
 * lists, etc.
 */
import { computed } from 'vue';
import { ChevronLeft, ChevronRight } from 'lucide-vue-next';

const props = withDefaults(
  defineProps<{
    page: number;
    pageSize: number;
    total: number;
    pageSizeOptions?: number[];
  }>(),
  { pageSizeOptions: () => [10, 25, 50, 100] },
);

defineEmits<{ 'update:page': [n: number]; 'update:pageSize': [n: number] }>();

const totalPages = computed(() => Math.max(1, Math.ceil(props.total / props.pageSize)));
const startRow = computed(() => (props.total === 0 ? 0 : (props.page - 1) * props.pageSize + 1));
const endRow = computed(() => Math.min(props.page * props.pageSize, props.total));
const canPrev = computed(() => props.page > 1);
const canNext = computed(() => props.page < totalPages.value);
</script>

<template>
  <div class="flex flex-wrap items-center justify-between gap-3 px-2 py-2 text-md text-app-muted">
    <div class="flex items-center gap-2">
      <label class="flex items-center gap-2">
        <span>Rows per page</span>
        <select
          :value="pageSize"
          class="rounded-lg border border-app-border bg-app-surface px-2 py-1 text-app-fg focus:outline-none focus:ring-3 focus:ring-primary/20"
          @change="$emit('update:pageSize', Number(($event.target as HTMLSelectElement).value))"
        >
          <option v-for="opt in pageSizeOptions" :key="opt" :value="opt">{{ opt }}</option>
        </select>
      </label>
    </div>

    <div class="flex items-center gap-3">
      <span>{{ startRow }}–{{ endRow }} of {{ total }}</span>
      <div class="flex items-center gap-1">
        <button
          type="button"
          :disabled="!canPrev"
          class="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-app-border bg-app-surface text-app-muted hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Previous page"
          @click="canPrev && $emit('update:page', page - 1)"
        >
          <ChevronLeft class="h-4 w-4" aria-hidden="true" />
        </button>
        <button
          type="button"
          :disabled="!canNext"
          class="inline-flex h-8 w-8 items-center justify-center rounded-lg border border-app-border bg-app-surface text-app-muted hover:bg-slate-50 disabled:cursor-not-allowed disabled:opacity-40"
          aria-label="Next page"
          @click="canNext && $emit('update:page', page + 1)"
        >
          <ChevronRight class="h-4 w-4" aria-hidden="true" />
        </button>
      </div>
    </div>
  </div>
</template>
